// utils/fcmUtils.js
// 🔔 Firebase Cloud Messaging Utilities (web-only; legacy)
// v2: Multi-device token support

import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { supabase } from '../supabaseClient';

// Firebase configuration
// ---- Your Firebase config (kept exactly as provided) ----
const firebaseConfig = {
  apiKey:
    process.env.REACT_APP_FIREBASE_API_KEY ||
    'AIzaSyDCERM2CxTrk9-w60XB7nYL42RiQ6hSl1s',
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    'gweffit.firebaseapp.com',
  projectId:
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'gweffit',
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    'gweffit.appspot.com',
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_SENDER_ID ||
    '861803836871',
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID ||
    '1:861803836871:web:eaa22d0aeadf4db6cc15e2',
};

// Public VAPID key (env first, fallback kept)
const VAPID_KEY =
  process.env.REACT_APP_FIREBASE_VAPID_KEY ||
  'BFL5HxqRitHO7a0A4qMJoqscWf-VS7_QYp606z_lc6RDuGMWzVcRI2wbXBoNnaypdNqfYZMVB1OvksK8N_nrQu8';

// Singleton instances
let firebaseApp = null;
let messaging = null;
const tokenRegisterInFlight = new Set();
const tokenRegisterCooldown = new Map();

/**
 * Detect device type for token registration
 */
function detectDeviceType() {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  
  // Check if PWA (standalone mode)
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                window.navigator.standalone === true;
  
  // iOS detection
  const isIOS = /iPad|iPhone|iPod/.test(ua) || 
                (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Android detection
  const isAndroid = /Android/.test(ua);
  
  // Browser detection
  const isChrome = /Chrome/.test(ua) && !/Edge/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  const isEdge = /Edge/.test(ua);
  
  if (isIOS && isPWA) return 'ios_pwa';
  if (isIOS && isSafari) return 'ios_safari';
  if (isAndroid && isPWA) return 'android_pwa';
  if (isAndroid && isChrome) return 'android_chrome';
  if (isChrome) return 'chrome';
  if (isFirefox) return 'firefox';
  if (isSafari) return 'safari';
  if (isEdge) return 'edge';
  
  return 'unknown';
}

/**
 * Generate a simple device fingerprint for identification
 * This helps identify the same device across sessions
 */
function getDeviceFingerprint() {
  const components = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + 'x' + window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset()
  ];
  
  // Simple hash function
  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
}

/**
 * Initialize Firebase app
 */
export function initializeFirebaseMessaging() {
  if (firebaseApp) return firebaseApp;
  
  if (getApps().length === 0) {
    firebaseApp = initializeApp(firebaseConfig);
    console.log('🔥 Firebase Messaging initialized');
  } else {
    firebaseApp = getApps()[0];
  }
  
  return firebaseApp;
}

/**
 * Check if push notifications are supported
 */
export async function isPushSupported() {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  if (!('serviceWorker' in navigator)) return false;
  
  try {
    return await isSupported();
  } catch (e) {
    return false;
  }
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported');
  }
  
  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Get FCM token for this device
 */
export async function getFCMToken() {
  const supported = await isPushSupported();
  if (!supported) {
    throw new Error('Push notifications not supported');
  }
  
  if (!VAPID_KEY) {
    throw new Error('VAPID key not configured');
  }
  
  const app = initializeFirebaseMessaging();
  messaging = getMessaging(app);
  
  // Register service worker
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  
  // Get token
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration
  });
  
  return token;
}

/**
 * Save FCM token to database (multi-device support)
 * This upserts into user_fcm_tokens table
 */
export async function saveFCMTokenToDatabase(userId, token) {
  if (!userId || !token) {
    console.warn('Missing userId or token for FCM save');
    return { success: false, error: 'Missing data' };
  }
  
  const deviceType = detectDeviceType();
  const deviceFingerprint = getDeviceFingerprint();
  const dedupeKey = `${userId}:web_${deviceFingerprint}:${token}`;
  const now = Date.now();
  const lastAt = tokenRegisterCooldown.get(dedupeKey) || 0;
  if (now - lastAt < 60_000) {
    console.log('[push] token_register_skipped_duplicate', {
      reason: 'cooldown_fcm_utils',
      user_id: userId,
      device_id: `web_${deviceFingerprint}`,
    });
    return { success: true, skipped: true };
  }
  if (tokenRegisterInFlight.has(dedupeKey)) {
    console.log('[push] token_register_skipped_duplicate', {
      reason: 'in_flight_fcm_utils',
      user_id: userId,
      device_id: `web_${deviceFingerprint}`,
    });
    return { success: true, skipped: true };
  }
  tokenRegisterInFlight.add(dedupeKey);
  
  try {
    const registerPayload = {
      p_user_id: userId,
      p_device_id: `web_${deviceFingerprint}`,
      p_fcm_token: token,
      p_platform: deviceType,
      p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      p_now: new Date().toISOString(),
    };
    const { data, error } = await supabase.rpc('register_user_fcm_token', registerPayload);
    if (error) throw error;

    if (data?.conflict_resolved) {
      console.log('ℹ️ FCM token conflict resolved');
    }
    tokenRegisterCooldown.set(dedupeKey, Date.now());
    console.log(`✅ FCM token saved for device: ${deviceType}`);
    return { success: true, deviceType };
  } catch (err) {
    console.error('Error saving FCM token:', err);
    return { success: false, error: err.message };
  } finally {
    tokenRegisterInFlight.delete(dedupeKey);
  }
}

/**
 * Remove FCM token (for logout or disable)
 */

/**
 * Complete setup: request permission + get token + save to database
 */
export async function setupPushNotifications(userId) {
  try {
    // 1. Check/request permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      return { success: false, reason: 'permission_denied' };
    }
    
    // 2. Get FCM token
    const token = await getFCMToken();
    if (!token) {
      return { success: false, reason: 'no_token' };
    }
    
    // 3. Save to database (multi-device)
    const saveResult = await saveFCMTokenToDatabase(userId, token);
    
    return { 
      success: saveResult.success, 
      token,
      deviceType: saveResult.deviceType
    };
    
  } catch (err) {
    console.error('Push notification setup failed:', err);
    return { success: false, reason: err.message };
  }
}

/**
 * Setup handler for foreground messages
 */
export function setupForegroundMessageHandler(callback) {
  if (!messaging) {
    const app = initializeFirebaseMessaging();
    messaging = getMessaging(app);
  }
  
  return onMessage(messaging, (payload) => {
    console.log('📬 Foreground message received:', payload);
    if (callback) callback(payload);
  });
}

/**
 * Send push notification to a user (all their devices)
 * This is typically called from Edge Function, but can be used for testing
 */
// Intentionally no default export to keep API surface explicit.
